import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FiSave, FiRotateCcw, FiAlertCircle, FiCheckCircle, FiShoppingCart } from 'react-icons/fi';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import { createShopItem } from '../../../../store/shopItems/shopItemsSlice';

const CATEGORIES = ['سلاح', 'درع', 'خوذه', 'حذاء', 'حلق', 'قلادة'];

const Page = styled.div`
  padding: 28px 24px;
  max-width: 760px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  .icon {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(147,51,234,0.12); border: 1px solid rgba(147,51,234,0.25);
    display: flex; align-items: center; justify-content: center;
    svg { color: #a78bfa; font-size: 20px; }
  }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;

const Card = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 28px 24px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  > div { flex: 1; }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  label {
    display: block; font-size: 12px; font-weight: 600; color: #9ca3af;
    margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em;
  }
  input, textarea, select {
    width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; padding: 10px 13px; color: #e2e8f0; font-size: 13.5px;
    font-family: inherit; outline: none; transition: border-color 0.2s;
    &::placeholder { color: #374151; }
    &:focus { border-color: rgba(147,51,234,0.5); }
  }
  select option { background: #111; }
  textarea { resize: vertical; min-height: 110px; }
  .err { font-size: 12px; color: #f87171; margin-top: 5px; }
  .hint { font-size: 11.5px; color: #6b7280; margin-top: 5px; }
`;

const BtnRow = styled.div`
  display: flex; gap: 10px; justify-content: flex-end; margin-top: 28px;
`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 9px; border: none;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s;
  opacity: ${p => p.disabled ? 0.55 : 1};
  background: ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.12)' : '#7C3AED'};
  color: ${p => p.variant === 'reset' ? '#f87171' : '#fff'};
  border: 1px solid ${p => p.variant === 'reset' ? 'rgba(239,68,68,0.3)' : 'transparent'};
  &:hover { opacity: 0.85; }
`;

const AlertBox = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 20px;
  svg { color: #f87171; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fca5a5; }
`;

const InfoBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: rgba(251,191,36,0.07); border: 1px solid rgba(251,191,36,0.2);
  border-radius: 9px; padding: 12px 16px; margin-bottom: 24px;
  svg { color: #fbbf24; font-size: 16px; flex-shrink: 0; margin-top: 1px; }
  .msg { font-size: 13px; color: #fde68a; line-height: 1.5; }
`;

const SuccessBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: 48px 24px; text-align: center;
  svg { font-size: 52px; color: #34d399; }
  .title { font-size: 20px; font-weight: 700; color: #e2e8f0; }
  .sub { font-size: 14px; color: #6b7280; max-width: 360px; }
`;

const AddShopItemPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(state => state.auth);
  const shopItems = useSelector(state => state.shopItems);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { type: 'بيع', category: 'سلاح', price_num: 0 }
  });

  if (!auth.isAuth) return <Navigate to="/login" />;

  const onSubmit = (values) => {
    const fd = new FormData();
    Object.entries(values).forEach(([key, val]) => {
      if (key !== 'images') fd.append(key, val ?? '');
    });
    if (values.images?.length) {
      Array.from(values.images).forEach(file => fd.append('images', file));
    }
    dispatch(createShopItem(fd)).unwrap().then(() => setSubmitted(true));
  };

  return (
    <WebsiteLayout>
      <Page>
        <PageHeader>
          <div className="icon"><FiShoppingCart /></div>
          <div>
            <div className="title">{t('website.add_shop_item.title')}</div>
            <div className="sub">{t('website.add_shop_item.sub')}</div>
          </div>
        </PageHeader>

        <Card>
          {submitted ? (
            <SuccessBox>
              <FiCheckCircle />
              <div className="title">{t('website.add_shop_item.success_title')}</div>
              <div className="sub">{t('website.add_shop_item.success_sub')}</div>
              <Btn onClick={() => navigate('/shop')} style={{ marginTop: 8 }}>
                {t('website.add_shop_item.go_list')}
              </Btn>
            </SuccessBox>
          ) : (
            <>
              <InfoBanner>
                <FiAlertCircle />
                <div className="msg">
                  {t('website.add_shop_item.pending_note')}
                  <br />{t('website.add_shop_item.commission_note')}
                </div>
              </InfoBanner>

              {shopItems.errors.length > 0 && (
                <AlertBox>
                  <FiAlertCircle />
                  <div className="msg">{shopItems.errors.map((e, i) => <div key={i}>{e?.msg}</div>)}</div>
                </AlertBox>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <FormGroup>
                    <label>{t('website.add_shop_item.ad_type')}</label>
                    <select {...register('type')}>
                      <option value="بيع">{t('website.shop.type_sell')}</option>
                      <option value="شراء">{t('website.shop.type_buy')}</option>
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_shop_item.category')}</label>
                    <select {...register('category')}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </FormGroup>
                </Row>

                <FormGroup>
                  <label>{t('website.add_shop_item.name')}</label>
                  <input placeholder={t('website.add_shop_item.name_placeholder')}
                    {...register('name', { required: t('validation.required', 'مطلوب') })} />
                  {errors.name && <div className="err">{errors.name.message}</div>}
                </FormGroup>

                <Row>
                  <FormGroup>
                    <label>{t('website.add_shop_item.price')}</label>
                    <input placeholder="5,000 وون" {...register('price')} />
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_shop_item.price_num')}</label>
                    <input type="number" {...register('price_num')} />
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup>
                    <label>{t('website.add_shop_item.server')}</label>
                    <input placeholder="الكل" {...register('server')} />
                  </FormGroup>
                  <FormGroup>
                    <label>{t('website.add_shop_item.seller')}</label>
                    <input placeholder={t('website.add_shop_item.seller_placeholder')} {...register('seller')} />
                  </FormGroup>
                </Row>

                <FormGroup>
                  <label>{t('website.add_shop_item.contact_discord')}</label>
                  <input placeholder="discord.gg/..." {...register('contact_discord')} />
                </FormGroup>

                <FormGroup>
                  <label>{t('website.add_shop_item.description')}</label>
                  <textarea placeholder={t('website.add_shop_item.description_placeholder')} {...register('description')} />
                </FormGroup>

                <FormGroup>
                  <label>{t('website.add_shop_item.images')}</label>
                  <input type="file" accept="image/*" multiple {...register('images')} style={{ padding: '6px' }} />
                  <div className="hint">{t('website.add_shop_item.images_hint')}</div>
                </FormGroup>

                <BtnRow>
                  <Btn type="button" variant="reset" onClick={() => reset()} disabled={shopItems.isLoading}>
                    <FiRotateCcw /> {t('website.add_shop_item.reset')}
                  </Btn>
                  <Btn type="submit" disabled={shopItems.isLoading}>
                    <FiSave /> {shopItems.isLoading ? t('website.add_shop_item.saving') : t('website.add_shop_item.submit')}
                  </Btn>
                </BtnRow>
              </form>
            </>
          )}
        </Card>
      </Page>
    </WebsiteLayout>
  );
};

export default AddShopItemPage;
